import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios, { AxiosResponse } from 'axios';
import posthog from 'posthog-js';
import { getApiOrigin } from '../config/api';
import { AUTH_EXPIRED_EVENT, dispatchAuthExpired } from './authEvents';
import { fetchWalletNonce } from './walletNonce';

// MetaMask / window.ethereum type
declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: any[] }) => Promise<any>;
      isMetaMask?: boolean;
    };
  }
}

// Types
interface User {
  id: string;
  email: string;
  walletAddress?: string;
  role: 'Admin' | 'User' | 'Builder' | 'Trader' | 'Validator' | 'ReadOnly';
  lastLogin?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  logoutAll: () => Promise<{ revoked: number }>;
  register: (email: string, password: string, role?: string) => Promise<void>;
  loginWithWallet: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

interface LoginResponse {
  success: boolean;
  data?: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    user: User;
  };
  error?: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Configure axios defaults
// CRA dev: empty base keeps relative `/auth/*` + `/api/*` routed via package.json proxy
// Split-host preview: REACT_APP_API_ORIGIN=http://127.0.0.1:8082
const API_ORIGIN = getApiOrigin();
axios.defaults.baseURL = API_ORIGIN || '';
// Don't use withCredentials for JWT-based auth
axios.defaults.withCredentials = false;

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clear error
  const clearError = () => setError(null);

  // Set auth header for axios
  const setAuthHeader = (token: string | null) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Handle logout — useCallback with empty deps because all references (state setters,
  // localStorage, posthog) are module-stable, so the closure never goes stale.
  const handleLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    setAuthHeader(null);
    localStorage.removeItem('mev_shield_token');
    localStorage.removeItem('mev_shield_refresh_token');
    localStorage.removeItem('mev_shield_user');
    // PostHog: reset identity on logout
    posthog.reset();
  }, []);

  // Initialize auth from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const savedToken = localStorage.getItem('mev_shield_token');
        const savedUser = localStorage.getItem('mev_shield_user');

        if (savedToken && savedUser) {
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
          setAuthHeader(savedToken);

          // Verify token is still valid
          try {
            const response: AxiosResponse<ApiResponse<User>> = await axios.get('/auth/me');
            if (response.data.success && response.data.data) {
              setUser(response.data.data);
            } else {
              // Token is invalid, clear auth
              handleLogout();
            }
          } catch (error) {
            // Token is invalid, clear auth
            handleLogout();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        handleLogout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [handleLogout]);

  // ADM-131: clear session when any source dispatches auth-expired
  useEffect(() => {
    const onExpired = () => handleLogout();
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [handleLogout]);

  // Set up axios interceptor for token refresh + auth-expired bridge
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't attempt refresh when the 401 came from an auth endpoint itself
          // (e.g. wrong login credentials) — leave that to the form error path.
          const isAuthEndpoint = /^\/auth\//.test(originalRequest.url ?? '');
          if (isAuthEndpoint) {
            return Promise.reject(error);
          }

          const storedRefreshToken = localStorage.getItem('mev_shield_refresh_token');
          if (!storedRefreshToken) {
            // Mid-session 401 with no refresh token → session is dead
            dispatchAuthExpired({ reason: 'no_refresh_token' });
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            // /auth/refresh returns { data: { access_token, refresh_token } }
            // WITHOUT a top-level `success` field (Node/Python quirk).
            const response: AxiosResponse<ApiResponse<{ access_token: string; refresh_token?: string }>> =
              await axios.post('/auth/refresh', {
                refresh_token: storedRefreshToken
              });

            const payload = response.data?.data;
            const newToken = payload?.access_token;
            if (newToken) {
              setToken(newToken);
              setAuthHeader(newToken);
              localStorage.setItem('mev_shield_token', newToken);
              // Persist rotated refresh token — server uses single-use rotation;
              // old token is revoked, must store the new one or next refresh fails
              if (payload.refresh_token) {
                localStorage.setItem('mev_shield_refresh_token', payload.refresh_token);
              }

              // Retry original request with new token
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }

            dispatchAuthExpired({ reason: 'refresh_failed' });
          } catch {
            // Refresh failed — emit auth-expired; AuthContext + RequireAuth react
            dispatchAuthExpired({ reason: 'refresh_failed' });
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [handleLogout]);

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response: AxiosResponse<LoginResponse> = await axios.post('/auth/login', {
        email,
        password,
      });

      if (response.data.success && response.data.data) {
        const { access_token, refresh_token, user: userData } = response.data.data;
        
        setToken(access_token);
        setUser(userData);
        setAuthHeader(access_token);

        // Store in localStorage
        localStorage.setItem('mev_shield_token', access_token);
        localStorage.setItem('mev_shield_refresh_token', refresh_token);
        localStorage.setItem('mev_shield_user', JSON.stringify(userData));

        // PostHog: identify user after successful login
        posthog.identify(userData.id, { email: userData.email, role: userData.role });
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      // Call logout endpoint to blacklist token
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      handleLogout();
    }
  };

  // Revoke ALL refresh tokens for this user across every device.
  // Caller's stateless JWT is still valid until expiry, but no new ones can
  // be minted anywhere — including here, so we drop the local session too.
  const logoutAll = async (): Promise<{ revoked: number }> => {
    let revoked = 0;
    try {
      const res = await axios.post<ApiResponse<{ revoked: number }>>('/auth/logout-all');
      revoked = res.data?.data?.revoked ?? 0;
    } catch (error) {
      console.error('Logout-all API error:', error);
      throw error;
    } finally {
      handleLogout();
    }
    return { revoked };
  };

  // Register function
  const register = async (email: string, password: string, role = 'User'): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response: AxiosResponse<ApiResponse> = await axios.post('/auth/register', {
        email,
        password,
        role,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Registration failed';
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      setError(null);

      const response: AxiosResponse<ApiResponse> = await axios.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Password change failed');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Password change failed';
      setError(message);
      throw new Error(message);
    }
  };

  // Request password reset function
  const requestPasswordReset = async (email: string): Promise<void> => {
    try {
      setError(null);

      const response: AxiosResponse<ApiResponse> = await axios.post('/auth/reset-password', {
        email,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Password reset request failed');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Password reset request failed';
      setError(message);
      throw new Error(message);
    }
  };

  // MetaMask / wallet login (EIP-4361)
  const loginWithWallet = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      if (!window.ethereum) {
        throw new Error('MetaMask not installed. Please install MetaMask to use this option.');
      }

      // 1. Request wallet access
      const accounts: string[] = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const address = accounts[0];
      if (!address) throw new Error('No wallet account found.');

      // 2. Get nonce from backend (route is at /auth/wallet/nonce, outside /api prefix).
      //    POST {address} only (walletNonce.ts); the GET alias was removed in W10 S10.4.
      const { message } = await fetchWalletNonce(address);

      // 3. Sign message with MetaMask
      const signature: string = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, address],
      });

      // 4. Verify signature and get JWT
      const verifyResponse: AxiosResponse<LoginResponse> = await axios.post('/auth/wallet/verify', {
        address,
        signature,
      });

      if (!verifyResponse.data.success || !verifyResponse.data.data) {
        throw new Error(verifyResponse.data.error || 'Wallet authentication failed.');
      }

      const { access_token, refresh_token, user: userData } = verifyResponse.data.data;

      // 5. Store — identical to email login path
      setToken(access_token);
      setUser(userData);
      setAuthHeader(access_token);
      localStorage.setItem('mev_shield_token', access_token);
      localStorage.setItem('mev_shield_refresh_token', refresh_token);
      localStorage.setItem('mev_shield_user', JSON.stringify(userData));

      // PostHog: identify user after successful wallet login
      posthog.identify(userData.id, { email: userData.email, role: userData.role });
    } catch (error: any) {
      // User rejected MetaMask prompt
      if (error.code === 4001) {
        const message = 'Signature rejected. Please approve the sign-in request in MetaMask.';
        setError(message);
        throw new Error(message);
      }
      const message = error.response?.data?.error || error.message || 'Wallet login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Computed values
  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.role === 'Admin';

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    logoutAll,
    register,
    loginWithWallet,
    changePassword,
    requestPasswordReset,
    isAuthenticated,
    isAdmin,
    isLoading,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook for checking permissions in components
export const usePermissions = () => {
  const { user, isAuthenticated } = useAuth();

  // Role hierarchy for access control
  const ROLE_HIERARCHY: Record<string, number> = {
    'ReadOnly': 1,
    'User': 2,
    'Trader': 2,
    'Builder': 3,
    'Validator': 3,
    'Admin': 4,
  };

  // Check if user has required access level
  const hasRequiredAccess = (userRole: string, requiredRole?: string, adminOnly?: boolean): boolean => {
    if (adminOnly) {
      return userRole === 'Admin';
    }
    
    if (!requiredRole) {
      return true; // No specific role required
    }
    
    const userLevel = ROLE_HIERARCHY[userRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  };

  const checkPermission = (requiredRole?: string, adminOnly?: boolean): boolean => {
    if (!isAuthenticated || !user) {
      return false;
    }
    
    return hasRequiredAccess(user.role, requiredRole, adminOnly);
  };

  return {
    isAuthenticated,
    user,
    canAccess: checkPermission,
    isAdmin: user?.role === 'Admin',
    isUser: user?.role === 'User' || user?.role === 'Admin',
    isBuilder: user?.role === 'Builder' || user?.role === 'Admin',
    isTrader: user?.role === 'Trader' || user?.role === 'Admin',
    isValidator: user?.role === 'Validator' || user?.role === 'Admin',
    isReadOnly: !!user, // All authenticated users can read
  };
};

// Export types for external use
export type { User, AuthContextType };